import { defineStore } from 'pinia'
import _, { camelCase, split } from 'lodash'
import type { IProgramVm, ISelectItem, IProgramList, IProgramPriceEstimate } from '@/types'
import type { SortType } from 'vue3-easy-data-table'
import type { IProgramSessionVm } from '@/types/IProgramSessionVm'
import { nanoid } from 'nanoid'
import { formatMomentTime } from '@/tools/utils'
import moment from 'moment'
import type { IProgramDelivererVm } from '@/types/IProgramDelivererVm'
import type { IProgramParticipantVm } from '@/types/IProgramParticipantVm'
import type { IParticipationHowFoundVm } from '@/types/IParticipationHowFoundVm'
import type { IGolfausProgramRegistrationVm } from '@/types/IGolfausProgramRegistrationVm'
import type { IEthnicityVm } from '@/types/IEthnicityVm'

export const useProgramStore = defineStore({
  id: 'useProgramStore',
  state: () => ({
    rootUrl: '/api/Participation/',
    programs: [] as Array<IProgramVm>,
    programsSelector: [] as Array<ISelectItem>,
    regionsSelector: [] as Array<ISelectItem>,
    isLoadingRegionsSelector: false,
    participants: [] as Array<IProgramParticipantVm>,
    regions: [] as any,
    totalCount: 0,
    participationRegistration: null,
    programData: null as any,
    isLoadingPrograms: false,
    isLoadingProgramParticipants: false,
    isLoadingRegisteredParticipants: false,
    isLoadingProgram: false,
    isSavingProgram: false,
    isLoadingHowFound: false,
    isLoadingRegistrationdetail: false,
    isSavingRegistration: false,
    howFounds: Array<IParticipationHowFoundVm>(),
    programsTableHeaders: [
      { text: 'Program Period', value: 'startDate', sortable: true },
      { text: 'Category', value: 'category', sortable: true },
      { text: 'Program Name', value: 'programName', sortable: true },
      { text: 'Registration Cut Off', value: 'registrationCutOffDateUTC', sortable: true },
      { text: 'Day / Time', value: 'days', sortable: true },
      { text: 'Sessions', value: 'sessions', sortable: true },
      { text: 'Cost', value: 'cost', sortable: true },
      { text: 'Participants', value: 'registeredParticipants', sortable: false },
      { text: '', value: 'buttons', width: 110, sortable: false }
    ],
    programOptions: [] as Array<IProgramList>,
    isLoadingRegistration: false,
    publicWebsiteUrl: '',
    isLoadingEthnicity: false,
    ethnicities: Array<IEthnicityVm>()
  }),

  actions: {
    async getPrograms(
      searchByName?: string,
      fromDate?: string,
      toDate?: string,
      searchByTermId?: number,
      searchByCategoryId?: number,
      page?: number,
      rowsPerPage?: number,
      sortBy?: string | string[],
      sortType?: SortType | SortType[],
      venueId?: number,
      searchByDateRadio?: number,
      isAssociationUser?: boolean,
    ) {
      if(!venueId && isAssociationUser) {
        this.totalCount = 0;
        this.programs = [];
        
        return;
      }

      if (this.isLoadingPrograms) {
        return
      }

      this.isLoadingPrograms = true
      try {
        var url = `${this.$state.rootUrl}GetPrograms?page=${page}&rowsPerPage=${rowsPerPage}&sortBy=${sortBy}&sortType=${sortType}`

        if (searchByName) url += `&name=${searchByName}`
        if (fromDate) url += `&fromDate=${fromDate}`
        if (toDate) url += `&toDate=${toDate}`
        if (searchByTermId) url += `&termId=${searchByTermId}`
        if (searchByCategoryId) url += `&categoryId=${searchByCategoryId}`
        if (venueId) url += `&venueId=${venueId}`
        if (searchByDateRadio) url += `&searchByDateRadio=${searchByDateRadio}`

        const response = await this.axiosFactory.withoutToken().get(url)
        this.totalCount = response.data.data.totalCount
        this.programs = response.data.data.data
      } catch (error: any) {
        console.log(error.response)
      } finally {
        this.isLoadingPrograms = false
      }
    },
    async removeProgram(programUID: string): Promise<any> {
      try {
        const response = await this.axiosFactory
          .withoutToken()
          .post(`${this.$state.rootUrl}RemoveProgram?programUID=${programUID}`)
        if (!response.data?.success) {
          console.log('API Error Message: ', response.data?.errorMessage)
        }

        return response.data?.data
      } catch (error) {
        console.log(error)
      }
    },
    async getProgramTableHeaders() {
      var url = `${this.$state.rootUrl}GetProgramTableHeaders`
      const response = await this.axiosFactory.withoutToken().get(url)
      this.programsTableHeaders = response.data.data.map((x: any) => {
        return {
          ...x,
          value: camelCase(x.value)
        }
      })
    },
    async saveProgram(program: IProgramVm): Promise<boolean> {
      try {
        this.isSavingProgram = true
        var url = `${this.$state.rootUrl}CreateUpdateProgram`
        const response = await this.axiosFactory.withoutToken().post(url, program)

        if (response.data.success) {
          return response.data?.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isSavingProgram = false
      }

      return false
    },
    async getNewProgramPriceEstimate(advertisedPrice: number): Promise<IProgramPriceEstimate> {
      const response = await this.axiosFactory.withoutToken().get(
        `${this.$state.rootUrl}GetNewProgramPriceEstimate`,
        { params: { advertisedPrice } }
      )

      if (!response.data?.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Unable to calculate the programme price estimate.')
      }

      return response.data.data
    },
    async getProgramById(programUID: string, clubId = null) {
      try {
        if (!programUID) {
          return
        }

        if (this.isLoadingProgram) {
          return
        }

        this.isLoadingProgram = true
        var url = `${this.$state.rootUrl}GetProgramById?programUID=${programUID}${(clubId? '&clubId=' + clubId : '')}`
        const response = await this.axiosFactory.withoutToken().get(url)

        if (response.data.success) {
          var data = response.data?.data
          let arrayTime = split(data.sessionTimeFrom, ':').map((x) => parseInt(x))
          data.timeFrom = {
            hours: arrayTime[0],
            minutes: arrayTime[1],
            seconds: arrayTime[2]
          }

          arrayTime = split(data.sessionTimeTo, ':').map((x) => parseInt(x))
          data.timeTo = {
            hours: arrayTime[0],
            minutes: arrayTime[1],
            seconds: arrayTime[2]
          }

          data.programSessionsDisplay = data.programSessions.map((s: IProgramSessionVm) => {
            return {
              id: nanoid(),
              date: moment(s.sessionDate).format('DD MMM yyyy'),
              sessionDate: s.sessionDate,
              time: formatMomentTime(data.timeFrom) + ' - ' + formatMomentTime(data.timeTo)
            }
          })

          data.deliverers.forEach((d: IProgramDelivererVm) => {
            d.isValid = true
          })

          this.programData = Object.assign({}, data)
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoadingProgram = false
      }
    },
    async getProgramParticipants(
      programUID: string,
      name: string,
      page?: number,
      rowsPerPage?: number,
      sortBy?: string | string[],
      sortType?: SortType | SortType[]
    ) {
      try {
        if (!programUID) {
          return
        }

        if (this.isLoadingRegisteredParticipants) {
          return
        }

        this.isLoadingRegisteredParticipants = true

        var url = `${this.$state.rootUrl}GetPagedProgramsParticipantsByProgramUID?page=${page}&rowsPerPage=${rowsPerPage}&sortBy=${sortBy}&sortType=${sortType}`

        if (name) url += `&name=${name}`
        if (programUID) url += `&programUID=${programUID}`

        const response = await this.axiosFactory.withoutToken().get(url)
        if (response.data.success) {
          this.totalCount = response.data.data.totalCount
          this.participants = response.data.data.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoadingRegisteredParticipants = false
      }
    },
    async getAllParticipants(
      name: string,
      page?: number,
      rowsPerPage?: number,
      sortBy?: string | string[],
      sortType?: SortType | SortType[],
      programUID?: string,
      programCategoryId: number | null = null
    ) {
      try {
        if (this.isLoadingProgramParticipants) {
          return
        }

        this.isLoadingProgramParticipants = true
        var url = `${this.$state.rootUrl}GetPagedAllParticipants?page=${page}&rowsPerPage=${rowsPerPage}&sortBy=${sortBy}&sortType=${sortType}&programUID=${programUID}&programCategoryId=${programCategoryId}`

        if (name) url += `&name=${name}`

        const response = await this.axiosFactory.withoutToken().get(url)
        if (response.data.success) {
          this.totalCount = response.data.data.totalCount
          this.participants = response.data.data.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoadingProgramParticipants = false
      }
    },
    async getAllParticipantsOnAdd(
      name: string,
      page?: number,
      rowsPerPage?: number,
      sortBy?: string | string[],
      sortType?: SortType | SortType[],
      programUID?: string,
      programCategoryId: number | null = null
    ) {
      try {
        if (this.isLoadingProgramParticipants) {
          return
        }

        this.isLoadingProgramParticipants = true
        var url = `${this.$state.rootUrl}GetPagedAllParticipantsOnAdd?page=${page}&rowsPerPage=${rowsPerPage}&sortBy=${sortBy}&sortType=${sortType}&programUID=${programUID}&programCategoryId=${programCategoryId}`

        if (name) url += `&name=${name}`

        const response = await this.axiosFactory.withoutToken().get(url)
        if (response.data.success) {
          this.totalCount = response.data.data.totalCount
          this.participants = response.data.data.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoadingProgramParticipants = false
      }
    },
    async removeProgramParticipant(
      participationRegistrationDetailUID: string
    ): Promise<any> {
      try {
        const response = await this.axiosFactory
          .withoutToken()
          .post(
            `${this.$state.rootUrl}RemoveProgramParticipant?participationRegistrationDetailUID=${participationRegistrationDetailUID}`
          )
        if (!response.data?.success) {
          console.log('API Error Message: ', response.data?.errorMessage)
        }

        return response.data?.data
      } catch (error) {
        console.log(error)
      }
    },
    async registerSelectedParticipants(
      participationRegistrationDetailIds: Array<string>,
      programUID: string,
      isMoveParticipant: boolean
    ): Promise<boolean> {
      try {
        this.isSavingProgram = true
        const url = `${this.$state.rootUrl}RegisterSelectedParticipants?programUID=${programUID}&isMoveParticipant=${isMoveParticipant}`
        const response = await this.axiosFactory
          .withoutToken()
          .post(url, participationRegistrationDetailIds)

        if (response.data.success) {
          return response.data?.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isSavingProgram = false
      }

      return false
    },
    async getParticipationHowFound() {
      try {
        if (this.isLoadingHowFound) {
          return
        }

        this.isLoadingHowFound = true
        var url = `${this.$state.rootUrl}GetParticipationHowFound`
        const response = await this.axiosFactory.withoutToken().get(url)

        if (response.data.success) {
          this.howFounds = response.data?.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoadingProgram = false
      }
    },
    async createUpdateParticipationRegistration(
      registration: IGolfausProgramRegistrationVm
    ): Promise<any> {
      try {
        if (this.isSavingRegistration) return false

        this.isSavingRegistration = true
        var url = `${this.$state.rootUrl}CreateUpdateParticipationRegistration`
        const response = await this.axiosFactory.withoutToken().post(url, registration)

        if (response.data.success) {
          return true;
        }else{
          return response.data.errorMessage;
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isSavingRegistration = false
      }

      return false
    },
    async getParticipationRegistrationDetail(detailId: string) {
      try {
        if (this.isLoadingRegistrationdetail) {
          return
        }

        this.isLoadingRegistrationdetail = true
        var url = `${this.$state.rootUrl}GetParticipationRegistrationDetail?detailId=${detailId}`
        const response = await this.axiosFactory.withoutToken().get(url)
        if (response.data.success) {
          return response.data?.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoadingRegistrationdetail = false
      }
    },
    async getParticipationRegistrationById(id: number) {
      try {
        if (this.isLoadingRegistration) {
          return
        }

        this.isLoadingRegistration = true
        var url = `${this.$state.rootUrl}getParticipationRegistrationById?id=${id}`
        const response = await this.axiosFactory.withoutToken().get(url)
        if (response.data.success) {
          this.isLoadingRegistration = false
          return response.data?.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoadingRegistration = false
      }
    },
    async getRegions(regionId: number) {
      try {
        if (this.isLoadingRegistrationdetail) {
          return
        }

        this.isLoadingRegistrationdetail = true
        var url = `${this.$state.rootUrl}GetRegions?regionId=${regionId}`
        const response = await this.axiosFactory.withoutToken().get(url)
        if (response.data.success) {
          this.regions = response.data?.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoadingRegistrationdetail = false
      }
    },
    async loadRegionsSelector() {
      if (this.isLoadingRegionsSelector || this.regionsSelector.length) return

      try {
        this.isLoadingRegionsSelector = true
        const response = await this.axiosFactory
          .withoutToken()
          .get(`${this.$state.rootUrl}GetRegionsSelector`)

        if (response.data) {
          this.regionsSelector = response.data.data
        }
      } catch (error: any) {
        console.log(error, error.response)
      } finally {
        this.isLoadingRegionsSelector = false
      }
    },
    async loadProgramsSelector(programCategoryId: number, venueId: number | undefined) {
      try {
        if (this.isLoadingPrograms) return

        this.isLoadingPrograms = true
        var url = `${
          this.$state.rootUrl
        }GetAssociationProgramsSelector?programCategoryId=${programCategoryId}&venueId=${
          venueId ? venueId : ''
        }`
        const response = await this.axiosFactory.withoutToken().get(url)
        if (response.data.success) {
          this.programsSelector = response.data.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoadingPrograms = false
      }
    },

    async loadRelatedPrograms(programUID: string) {
      return await this.axiosFactory
      .withoutToken()
      .get(`${this.$state.rootUrl}GetRelatedPrograms?programUID=${programUID}`)
      .then((response) => {
        this.programOptions = response.data.data;
      })
      .catch((error) => {
        console.error("Get Related Programs failed", error);
      });
    },

    async postProgramParticipantHistory(programUID: string, newProgramUID: string, participationRegistrationDetailUID: string): Promise<any> {
      try {
        const response = await this.axiosFactory
          .withoutToken()
          .post(`${this.$state.rootUrl}PostProgramParticipantHistory?programUID=${programUID}&newProgramUID=${newProgramUID}&participationRegistrationDetailUID=${participationRegistrationDetailUID}`)
        if (!response.data?.success) {
          return response.data;
        }
        return response.data;
      } catch (error) {
        console.log(error)
      }
    },

    async getPublicWebsiteUrl() {
      const response = await this.axiosFactory
          .withoutToken()
          .get(`${this.$state.rootUrl}GetPublicWebsiteUrl`);

       if (response.data?.success) {
           this.publicWebsiteUrl = response.data.data;
           if(this.publicWebsiteUrl && !(this.publicWebsiteUrl as string).endsWith('/')) {
            this.publicWebsiteUrl += '/';
           }           
       }
    },
    async getEthnicities() {
      if (this.isLoadingEthnicity || this.ethnicities.length) return;

      try {
        this.isLoadingEthnicity = true;
        const response = await this.axiosFactory
          .withoutToken()
          .get(`${this.$state.rootUrl}GetEthnicities`);

        if (response.data) {
          this.ethnicities = response.data;
        }
      } catch (error: any) {
        console.log(error, error.response);
      } finally {
        this.isLoadingEthnicity = false;
      }
    },
  }
})
